/**
 * useSavoirCareBoard.js — real-time data hook for the SAVOIR CARE ED flow board.
 *
 * What: Subscribes to `savoirCareBoards/{facilityId}/patients` and exposes the
 *       live entry list plus role-gated write helpers (advance stage, flag,
 *       assign ESI, document a patient-elected redirect, manual add).
 * Why:  Centralizes board state + Firestore writes so board UI components stay
 *       presentational. Mirrors the `onSnapshot` + `return () => unsub?.()`
 *       pattern used across the app.
 * Invariants:
 *   - Facility scope is resolved from the signed-in user's location membership
 *     (never trusted from a URL). Cross-facility access is also blocked at the
 *     Firestore rules layer (defense in depth).
 *   - All persisted stage values are snake_case.
 *   - `patient_elected_redirect` is terminal: moveToStage refuses to move a card
 *     out of it, and only stages 2–4 may move into it.
 *   - Writes only target `savoirCareBoards/`; this hook never writes CareStart
 *     session docs.
 *
 * Ported from HealthDesk Experimental (Exp-B). MinusOne adaptations:
 *   - `db` from `services/firebase` (compat) instead of `@healthdesk/shared-services`.
 *   - facilityId resolved from `LocationContext` (locations/{id} membership doc)
 *     instead of `userData.facilityId` — MinusOne's facility model is the
 *     `locations` collection with a `users` membership array.
 *   - Write role is `facility` (the facility dashboard operator) plus global admin,
 *     instead of the Exp-B chartmind role set.
 * Tightest coupling: SavoirCareBoardPage.jsx.
 */

import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useAuth } from 'hooks/useAuth';
import { LocationContext } from 'context/Location';
import { db } from 'services/firebase';
import firebase from 'firebase/compat/app';
import {
  SAVOIR_CARE_STAGES,
  ESCALATION_STAGE,
  getNextClinicalStage,
  isRedirectEligible,
  canAdvanceFromExpected,
} from '../constants/savoirCareStages';

/** Minimum length for the required clinical-notes field on a redirect. */
const REDIRECT_NOTES_MIN_LENGTH = 10;

/** Max length of the free-text emergency intervention name. */
const INTERVENTION_NAME_MAX_LENGTH = 60;

/** Max length of the free-text physical_location label. Display only. */
const PHYSICAL_LOCATION_MAX_LENGTH = 40;

/** Convert a Firestore Timestamp (or raw value) to a JS Date for rendering. */
const toDate = (value) => value?.toDate?.() || (value instanceof Date ? value : null);

/** Epoch ms for sort: board arrival first, then doc creation; 0 when neither set. */
const arrivalMs = (entry) => {
  const d = entry.arrivedAt || entry.createdAt;
  return d instanceof Date ? d.getTime() : 0;
};

/**
 * Board sort comparator: emergency_intervention cards float to the TOP of their
 * column, then ties break by arrival time ascending (oldest first). This is a
 * pure client-side sort on the real-time snapshot — no Firestore index.
 */
const byEmergencyThenArrival = (a, b) => {
  const aEmerg = a.emergency_intervention === true ? 1 : 0;
  const bEmerg = b.emergency_intervention === true ? 1 : 0;
  if (aEmerg !== bEmerg) return bEmerg - aEmerg;
  return arrivalMs(a) - arrivalMs(b);
};

/**
 * Live SAVOIR CARE board state + write actions for the current user's facility.
 * @returns {object} board state and write action callbacks
 */
const useSavoirCareBoard = () => {
  const { user, userData, isGlobalAdmin } = useAuth();
  const { location } = useContext(LocationContext);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const retryLoad = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryNonce((n) => n + 1);
  }, []);

  // INVARIANT: facility scope comes from the authenticated user's location
  // membership doc, never a URL param. `location.id` is the locations/{id} doc id.
  const facilityId = location?.id;
  const userRole = userData?.role;
  const canWrite = userRole === 'facility' || Boolean(isGlobalAdmin);

  // Collection reference for this facility's board patients.
  const patientsCol = useMemo(() => {
    if (!facilityId) return null;
    return db.collection('savoirCareBoards').doc(facilityId).collection('patients');
  }, [facilityId]);

  // Real-time subscription.
  useEffect(() => {
    if (!patientsCol) {
      setError('Facility not found. Your account must be associated with a facility to use SAVOIR CARE.');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsub = patientsCol.orderBy('createdAt', 'asc').onSnapshot(
      (snapshot) => {
        const next = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            entryId: doc.id,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            sessionCreatedAt: toDate(data.sessionCreatedAt),
            arrivedAt: toDate(data.arrivedAt),
          };
        });
        // Soft-archived Expected (elective dismiss) stay out of the live board.
        const visible = next.filter((e) => e.archived !== true);
        visible.sort(byEmergencyThenArrival);
        setEntries(visible);
        setLoading(false);
      },
      (err) => {
        console.error('[SavoirCare] Board subscription error:', err);
        setError('Failed to load the SAVOIR CARE board.');
        setLoading(false);
      }
    );

    return () => unsub?.();
  }, [patientsCol, retryNonce]);

  /**
   * Append-only stage-history entry. Uses a client Timestamp (not
   * serverTimestamp) because Firestore forbids server sentinels inside arrays.
   */
  const buildHistoryEntry = useCallback(
    (stage) => ({
      stage,
      changedAt: firebase.firestore.Timestamp.now(),
      changedBy: user?.uid || null,
    }),
    [user?.uid]
  );

  /** Move a card to an explicit stage (used by drag-and-drop). Enforces invariants. */
  const moveToStage = useCallback(
    async (entry, newStage) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      if (entry.savoir_care_stage === newStage) return;

      // INVARIANT: redirect is terminal — never move a card out of it.
      if (entry.savoir_care_stage === SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT) {
        return;
      }
      // INVARIANT: entering redirect requires the documentation modal
      // (recordPatientElectedRedirect), never a bare stage move.
      if (newStage === SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT) {
        return;
      }

      // PROMPT 8 — Expected is CareVerify pre-arrival. Only urgent visitType may
      // advance into incoming/clinical stages; elective stays until dismissed.
      if (entry.savoir_care_stage === SAVOIR_CARE_STAGES.EXPECTED) {
        if (!canAdvanceFromExpected(entry)) return;
        if (
          newStage !== SAVOIR_CARE_STAGES.INCOMING &&
          newStage !== SAVOIR_CARE_STAGES.TRIAGE
        ) {
          return;
        }
      }
      // Do not drag cards back into Expected.
      if (newStage === SAVOIR_CARE_STAGES.EXPECTED) {
        return;
      }

      await patientsCol.doc(entry.entryId).update({
        savoir_care_stage: newStage,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        stageHistory: firebase.firestore.FieldValue.arrayUnion(buildHistoryEntry(newStage)),
      });
    },
    [patientsCol, canWrite, buildHistoryEntry]
  );

  /** Advance a card to the next clinical stage (primary movement path). */
  const advanceStage = useCallback(
    async (entry) => {
      if (entry?.savoir_care_stage === SAVOIR_CARE_STAGES.EXPECTED) {
        if (!canAdvanceFromExpected(entry)) return;
      }
      const next = getNextClinicalStage(entry?.savoir_care_stage);
      if (!next) return;
      await moveToStage(entry, next);
    },
    [moveToStage]
  );

  /**
   * Soft-archive an Expected-column entry (elective CareVerify visits that never
   * enter the ED clinical flow). Filtered out of the live board snapshot.
   */
  const dismissExpectedEntry = useCallback(
    async (entry) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      if (entry.savoir_care_stage !== SAVOIR_CARE_STAGES.EXPECTED) return;
      await patientsCol.doc(entry.entryId).update({
        archived: true,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  /** Set or clear the manual clinician flag color. */
  const setFlag = useCallback(
    async (entry, flagColor) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      await patientsCol.doc(entry.entryId).update({
        flagColor: flagColor || null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  /**
   * Assign (or clear) the clinician-set ESI level (1–5).
   * @returns {Promise<{shouldPromptEmergencyIntervention: boolean}>} signals the
   *   caller to offer the emergency-intervention prompt when ESI 1 is assigned to a
   *   card that is not already flagged (never auto-set silently).
   */
  const assignEsi = useCallback(
    async (entry, esi) => {
      if (!patientsCol || !canWrite || !entry?.entryId) {
        return { shouldPromptEmergencyIntervention: false };
      }
      const value = esi === null || esi === undefined ? null : Number(esi);
      await patientsCol.doc(entry.entryId).update({
        savoir_care_esi: value,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      const shouldPromptEmergencyIntervention =
        value === 1 && entry.emergency_intervention !== true;
      return { shouldPromptEmergencyIntervention };
    },
    [patientsCol, canWrite]
  );

  /**
   * Flag a card as an emergency intervention with the required free-text name.
   * Sets emergency_intervention true; the snapshot sort then floats the card to
   * the top of its column. May be raised at any point during the visit by any
   * write-role clinician.
   * @param {object} entry board entry being flagged
   * @param {string} interventionName e.g. "Cardioversion" — required, ≤ 60 chars
   */
  const flagEmergencyIntervention = useCallback(
    async (entry, interventionName) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      const name = (interventionName || '').trim().slice(0, INTERVENTION_NAME_MAX_LENGTH);
      // INVARIANT: emergency_intervention true requires a non-empty intervention name.
      if (!name) {
        throw new Error('An intervention name is required to flag an emergency intervention.');
      }
      await patientsCol.doc(entry.entryId).update({
        emergency_intervention: true,
        savoir_care_intervention_name: name,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  /** Clear the emergency intervention flag and its name (write roles only). */
  const clearEmergencyIntervention = useCallback(
    async (entry) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      await patientsCol.doc(entry.entryId).update({
        emergency_intervention: false,
        savoir_care_intervention_name: null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  /**
   * Raise or clear the APP→physician escalation flag. The escalation does NOT
   * force a stage change — the physician can review from any stage; it only
   * surfaces a visual flag on the card.
   * @param {object} entry board entry being (de)escalated
   * @param {boolean} escalated true to raise the flag, false to clear it
   */
  const setEscalated = useCallback(
    async (entry, escalated) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      // Guard: escalation is raised from the highest-risk APP-only step (stage 4).
      // De-escalation (clearing) is allowed from any stage so a physician who has
      // reviewed can lower the flag without first moving the card.
      if (escalated && entry.savoir_care_stage !== ESCALATION_STAGE) {
        throw new Error('Escalation can only be raised from the Initial review stage.');
      }
      await patientsCol.doc(entry.entryId).update({
        escalated: Boolean(escalated),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  /**
   * Document a Patient-Elected Redirect and move the card to the terminal
   * redirect column. The patient (already assessed) has been offered a complete
   * evaluation and chosen to leave for a next-day appointment — a patient choice,
   * NOT a clinical clearance.
   * @param {object} entry board entry being redirected
   * @param {object} doc collected modal fields (checkboxes + clinical notes)
   */
  const recordPatientElectedRedirect = useCallback(
    async (entry, doc) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;

      // Guard: only stages 2–4 are redirect-eligible.
      if (!isRedirectEligible(entry.savoir_care_stage)) {
        throw new Error('This patient cannot be redirected from the current stage.');
      }
      // Guard: required attestations + clinical notes must be present.
      const notesOk = (doc?.clinicalNotes || '').trim().length >= REDIRECT_NOTES_MIN_LENGTH;
      if (
        !doc?.patientAlertAndOriented ||
        !doc?.appointmentPathProvided ||
        !doc?.returnPrecautions ||
        !notesOk
      ) {
        throw new Error('All attestations and clinical notes (min 10 characters) are required.');
      }

      const stageAtElection = entry.savoir_care_stage;
      await patientsCol.doc(entry.entryId).update({
        savoir_care_stage: SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        stageHistory: firebase.firestore.FieldValue.arrayUnion(
          buildHistoryEntry(SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT)
        ),
        patientElectedRedirect: {
          electedAt: firebase.firestore.Timestamp.now(),
          recordedBy: user?.uid || null,
          recordedByRole: userRole || null,
          stageAtElection,
          offeredFullEval: true,
          patientDeclinedFullEval: true,
          patientAlertAndOriented: Boolean(doc.patientAlertAndOriented),
          appointmentPathProvided: Boolean(doc.appointmentPathProvided),
          clinicalNotes: doc.clinicalNotes.trim(),
          returnPrecautions: Boolean(doc.returnPrecautions),
        },
      });
    },
    [patientsCol, canWrite, buildHistoryEntry, user?.uid, userRole]
  );

  /**
   * Manual add: create a board entry with no CareStart linkage (walk-in fallback).
   * CareStart-sourced patients arrive via the check-in / bedside callables.
   * @param {{chiefComplaintSummary?: string, savoir_care_esi?: number|null, patientAge?: number|null}} data
   * @returns {Promise<string>} new entry doc ID
   */
  const createManualEntry = useCallback(
    async (data) => {
      if (!patientsCol || !canWrite) {
        throw new Error('You do not have permission to add patients to this board.');
      }
      const now = firebase.firestore.FieldValue.serverTimestamp();
      const ref = await patientsCol.add({
        facilityId,
        createdAt: now,
        updatedAt: now,
        // CareStart fields are null/false for a manual entry (no session linkage).
        careStartTokenId: null,
        hasCareStartData: false,
        careLevel: null,
        chiefComplaintSummary: (data?.chiefComplaintSummary || '').slice(0, 80),
        patientAge: data?.patientAge ?? null,
        diagnosticNeeds: [],
        // No CareStart session for a manual add; arrival time is "now" (walk-in).
        sessionCreatedAt: null,
        arrivedAt: now,
        // Manual adds start at the front-door physician triage step — not at
        // incoming, which is the pre-arrival front-desk column.
        savoir_care_stage: SAVOIR_CARE_STAGES.TRIAGE,
        savoir_care_esi: data?.savoir_care_esi ?? null,
        flagColor: null,
        escalated: false,
        emergency_intervention: false,
        savoir_care_intervention_name: null,
        // CareStart provenance: a manual walk-in has no token check-in.
        checkin_token_id: null,
        checkin_activated_by: null,
        checkin_activated_at: null,
        assignedTo: null,
        stageHistory: [buildHistoryEntry(SAVOIR_CARE_STAGES.TRIAGE)],
        patientElectedRedirect: null,
        chartmindEncounterId: null,
        // Free-text physical location label (display only — never drives clinical logic).
        physical_location: null,
        createdBy: user?.uid || null,
      });
      return ref.id;
    },
    [patientsCol, canWrite, facilityId, buildHistoryEntry, user?.uid]
  );

  /**
   * Set or clear the free-text physical location label. Display only — never
   * drives clinical logic. Preserved across stage advances; an empty/blank value
   * clears it to null. Capped at PHYSICAL_LOCATION_MAX_LENGTH chars.
   * @param {object} entry board entry to update
   * @param {string|null} value new location text, or empty/null to clear
   */
  const setPhysicalLocation = useCallback(
    async (entry, value) => {
      if (!patientsCol || !canWrite || !entry?.entryId) return;
      const trimmed = (value || '').trim().slice(0, PHYSICAL_LOCATION_MAX_LENGTH);
      await patientsCol.doc(entry.entryId).update({
        physical_location: trimmed || null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    [patientsCol, canWrite]
  );

  return {
    entries,
    loading,
    error,
    retryLoad,
    facilityId,
    canWrite,
    userRole,
    advanceStage,
    moveToStage,
    dismissExpectedEntry,
    setFlag,
    assignEsi,
    setEscalated,
    flagEmergencyIntervention,
    clearEmergencyIntervention,
    recordPatientElectedRedirect,
    createManualEntry,
    setPhysicalLocation,
  };
};

export default useSavoirCareBoard;
